import {AgendaelementId, BeslutningITranskribertSaksreferatModell, GruppeMedlemListeModell, HtmlTekst, MøteModell, OppgaveITranskribertSaksreferatModell, OppsummertTranskribertMøtereferatModell, OppsummertTranskribertSaksreferatModell} from "../tjenester/ressurser/generertNg2HttpKlient";
import {erNil} from "../støttefunksjoner/nilsjekk";
import {lesPlainDateSerialisertOpt} from "../støttefunksjoner/temporal";
import {AgendaelementModellDLM} from "../tjenester/datalager/SakDatatjeneste";
import {Temporal} from "temporal-polyfill";
import PlainDate = Temporal.PlainDate;

/**
 * Sann dersom innlogget bruker har tilgang til å generere, og se, forslag til protokoll fra lydopptakene av angitt møte.
 *
 * Lydopptakene, og dermed forslaget, omfatter hele møtebehandlingen. Bruker som er skjermet for noen av sakene i møtet,
 * eller for noe i sakene (feks dokumenter), har derfor ikke tilgang.
 * Må holdes i synk med MøteTilgang.harIkkeOppsummerOpptakTilgang på server.
 */
export function harTilgangTilProtokollforslag(møte: Pick<MøteModell, 'tilgang'>): boolean {
    return !møte.tilgang.skjerming.inneholderBarnSkjermetForKallendeBruker;
}

/**
 * Returnerer en {@link GenerertMøtereferat} struktur basert på angitt oppsummertTranskripsjon og liste over saker i møtet referatet gjelder for.
 *
 * Hver sak i angitt oppsummertTranskripsjon parres med sak i angitt saksliste, dersom ingen match finnes i angitt saksliste legges refereratet
 * til i {@link GenerertMøtereferat#utenSak}
 */
export function somGenerertMøtereferat(oppsummertTranskripsjon: OppsummertTranskribertMøtereferatModell, saker: AgendaelementModellDLM[]): GenerertMøtereferat {
    const sakIderPåMøtet = new Set(saker.map(sak => sak.id));
    const sakerOgReferat = new Map<AgendaelementId, GenerertSaksreferat>();
    const utenSak: GenerertSaksreferat[] = [];

    const saksreferater = oppsummertTranskripsjon.saker.map(saksreferat => somGenerertSaksreferat(saksreferat));
    saksreferater.forEach(saksreferat => {
        if (erNil(saksreferat.sakId) || !sakIderPåMøtet.has(saksreferat.sakId) || sakerOgReferat.has(saksreferat.sakId))
            utenSak.push(saksreferat);
        else
            sakerOgReferat.set(saksreferat.sakId, saksreferat);
    });

    return {saker: sakerOgReferat, utenSak};
}

export function somGenerertSaksreferat(saksreferat: OppsummertTranskribertSaksreferatModell): GenerertSaksreferat {
    return {
        sakId: saksreferat.sakId,
        tittel: saksreferat.tittel,
        oppsummering: saksreferat.oppsummering,
        beslutninger: saksreferat.beslutninger.map(beslutning => somGenerertBeslutning(beslutning))
    };
}

  function somGenerertBeslutning(beslutning: BeslutningITranskribertSaksreferatModell): GenerertBeslutning {
    return {
        tittel: beslutning.tittel,
        beskrivelse: beslutning.beskrivelse,
        oppgaver: beslutning.oppgaver.map(oppgave => somGenerertOppgave(oppgave))
    };
}

 function somGenerertOppgave(oppgave: OppgaveITranskribertSaksreferatModell): GenerertOppgave {
    return {
        tittel: oppgave.tittel,
        beskrivelse: oppgave.beskrivelse,
        ansvarlig: oppgave.ansvarlig,
        frist: lesPlainDateSerialisertOpt(oppgave.frist)
    };
}

// modeller

/**
 * Et KI-generert saksreferat, klart til visning og innliming i protokollskjemaet.
 *
 * Tekstene er html, konvertert fra markdown (som er det LLM-en svarer med) og sanitert på serveren,
 * se MarkdownTekst.somHtml() i lederplan-tjenester.
 */
export interface GenerertSaksreferat {
    sakId: AgendaelementId | null;
    /** Sakstittelen slik den ble tolket av LLMen, mest aktuell for GenerertSaksreferat-innslag der vi ikke fant noen matchende sak i agendaen */
    tittel: string;
    /** Oppsummering av hva som ble diskutert i saken, null når serveren ikke oppsummerte den */
    oppsummering: HtmlTekst;
    beslutninger: GenerertBeslutning[];
}

/** En beslutning i et {@link GenerertSaksreferat}. */
export interface GenerertBeslutning {
    tittel: string;
    beskrivelse: HtmlTekst;
    oppgaver: GenerertOppgave[];
}

/** En oppgave under en {@link GenerertBeslutning} */
export interface GenerertOppgave {
    tittel: string;
    beskrivelse: HtmlTekst;
    /** Medlemmet serveren kjente igjen som ansvarlig, null når ingen ansvarlig ble navngitt */
    ansvarlig: GruppeMedlemListeModell | null;
    frist: PlainDate | null;
}

/**
 * Referatet KI-en genererte for et helt møte, fordelt på sakene som ble behandlet.
 *
 * Serveren merker hvert saksreferat med saken det gjelder, men den kan la være å merke det eller
 * peke på en sak som ikke ligger på møtet. Slike referater havner i {@link utenSak} framfor å bli
 * forkastet – brukeren skal få se alt opptaket ga.
 */
export interface GenerertMøtereferat {
    /** Saksreferatene som lot seg parre med en sak på møtet, nøklet på saken */
    saker: Map<AgendaelementId, GenerertSaksreferat>;
    /** Saksreferatene som ikke hører til noen sak på møtet, i den rekkefølgen serveren oppsummerte dem */
    utenSak: GenerertSaksreferat[];
}
